// hooks/use-shell-actions.js — Thread CRUD + workspace solo creation
// ═══════════════════════════════════════════════════════════════════
// Extracted from pages/index.js to keep the shell under 300 lines.
// Pure action functions — no state, no rendering, no side effects.
// ═══════════════════════════════════════════════════════════════════

import { classifyWorkspaceProtocol } from '../lib/workspace-protocols';
import { generateId } from '../lib/render-helpers';
import { resolveArtifactOrigin, scrollToMessageWhenLoaded } from '../lib/artifact-navigate';

export function useShellActions({
  session,
  supabase,
  spaceHook,
  setUserRecord,
  setCurrentSessionId,
  setPendingThreadId,
  setPendingNewThread,
  setThreadHistory,
  setIsIncognitoThread,
  setShowSoloModal,
  setCreatingSolo,
  currentSessionId,
  navigate,
}) {
  const userId = session?.user?.id;

  function authHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
    };
  }

  // ── Thread management ──────────────────────────────

  function handleNewThread(incognito = false) {
    spaceHook.closeSpaceSession();
    setIsIncognitoThread(incognito);
    setCurrentSessionId(generateId());
    setPendingNewThread(true);
    navigate('chat');
  }

  function handleLoadThread(sessionId, isIncognito) {
    setIsIncognitoThread(isIncognito === true);
    if (spaceHook.activeSpaceInfo) spaceHook.closeSpaceSession();
    setPendingThreadId(sessionId);
    navigate('chat');
  }

  // Jump to the thread where an artifact or note was born. Dispatches by
  // session_type (chat / space / workspace). After navigation
  // kicks off, polls for the birth message element and scrolls to it.
  // Works for any entity shaped { session_id, message_id } — artifacts
  // and notes share the same shape and navigation needs.
  async function handleArtifactNavigate(artifact) {
    if (!artifact) return;
    try {
      const origin = await resolveArtifactOrigin(artifact, supabase);
      const messageId = artifact.message_id || null;

      if (origin.kind === 'chat') {
        handleLoadThread(origin.sessionId, false);
      } else if ((origin.kind === 'workspace' || origin.kind === 'space') && origin.sharedSpace) {
        spaceHook.openSpaceSession({ ...origin.sharedSpace }, true);
        navigate(origin.kind === 'workspace' ? 'workspace' : 'chat');
      } else if (origin.kind === 'artifact') {
        // Legacy per-artifact chat session — fall back to regular chat load
        handleLoadThread(origin.sessionId, false);
      } else {
        navigate('chat');
      }

      if (messageId) scrollToMessageWhenLoaded(messageId);
    } catch (e) {
      console.error('handleArtifactNavigate:', e);
    }
  }

  async function handleLeaveIncognito() {
    const oldId = currentSessionId;
    setIsIncognitoThread(false);
    setCurrentSessionId(generateId());
    setPendingNewThread(true);
    localStorage.removeItem('shift_last_session');
    setThreadHistory(prev => prev.filter(t => t.id !== oldId));
    try {
      await fetch('/api/user/update', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action: 'delete_thread', payload: { sessionId: oldId } }),
      });
    } catch { /* non-fatal */ }
  }

  async function deleteThread(sessionId) {
    try {
      await fetch('/api/user/update', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action: 'delete_thread', payload: { sessionId } }),
      });
      setThreadHistory(prev => prev.filter(t => t.id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(generateId());
        setPendingNewThread(true);
        localStorage.removeItem('shift_last_session');
      }
    } catch (e) { console.error('deleteThread failed:', e); }
  }

  async function renameThread(sessionId, newTitle) {
    try {
      await fetch('/api/user/update', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action: 'rename_thread', payload: { sessionId, title: newTitle } }),
      });
      setThreadHistory(prev => prev.map(t => t.id === sessionId ? { ...t, title: newTitle } : t));
    } catch (e) { console.error('renameThread failed:', e); }
  }

  async function regenerateThreadTitle(sessionId) {
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action: 'regenerateTitle', sessionId, userId }),
      });
      const data = await res.json();
      if (res.ok && data.title) {
        setThreadHistory(prev => prev.map(t => t.id === sessionId ? { ...t, title: data.title } : t));
      } else {
        console.error('regenerateThreadTitle failed:', data.error || 'unknown');
      }
    } catch (e) { console.error('regenerateThreadTitle threw:', e); }
  }

  // ── Workspace solo creation ────────────────────────

  async function handleSoloCreate({ name, role, goal }) {
    setCreatingSolo(true);
    try {
      if (!userId) throw new Error('Not signed in.');

      const { data: newSpace, error: insertError } = await supabase
        .from('shared_spaces')
        .insert({
          name,
          space_type: 'space',
          created_by: userId,
          is_workspace: true,
          workspace_metadata: {
            solo: true,
            mode: 'solo',
            role: role || null,
            goal: goal || null,
            protocol: classifyWorkspaceProtocol({ title: name, role, goal }),
          },
        })
        .select()
        .single();
      if (insertError) throw new Error(insertError.message || 'Failed to create workspace.');

      await supabase.from('sessions').insert({
        id: newSpace.id,
        user_id: userId,
        session_type: 'workspace',
        created_at: new Date().toISOString(),
      });

      const { error: participantError } = await supabase
        .from('space_participants')
        .insert({ space_id: newSpace.id, user_id: userId });
      if (participantError) throw new Error(participantError.message || 'Failed to add participant.');

      setShowSoloModal(false);
      setCreatingSolo(false);

      spaceHook.openSpaceSession({
        ...newSpace,
        participants: [userId],
        others: [],
      });
    } catch (e) {
      setCreatingSolo(false);
      throw e;
    }
  }

  // ── Setup (onboarding) ─────────────────────────────

  async function handleSetup({ username, aiName }) {
    try {
      const { data: existing } = await supabase
        .from('users')
        .select('id')
        .eq('username', username)
        .maybeSingle();
      if (existing) return { error: 'Username already taken' };
      const { error } = await supabase
        .from('users')
        .update({ username, ai_name: aiName, onboarding_complete: true })
        .eq('id', userId);
      if (error) throw error;
      setUserRecord(prev => ({ ...prev, username, ai_name: aiName, onboarding_complete: true }));
      return { success: true };
    } catch (e) {
      console.error('handleSetup failed:', e);
      return { error: 'Could not save. Please try again.' };
    }
  }

  // Alias for semantic clarity at the call site. Notes use the same
  // dispatcher as artifacts because they share { session_id, message_id }.
  const handleNoteNavigate = handleArtifactNavigate;

  return {
    handleNewThread,
    handleLoadThread,
    handleArtifactNavigate,
    handleNoteNavigate,
    handleLeaveIncognito,
    deleteThread,
    renameThread,
    regenerateThreadTitle,
    handleSoloCreate,
    handleSetup,
    authHeaders,
  };
}
